import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { DirectMessage, Conversation, User } from '../types'
import MessageBubble from './MessageBubble'
import PrivateModeToggle from './PrivateModeToggle'
import { secureMessageService, DecryptedMessage } from '../services/secureMessageService'
import RateLimitWarning, { useRateLimit } from './RateLimitWarning'
import { requiresVendorApproval, createVendorContactRequest } from './VendorApprovalSystem'
import { messagingPatternDetection } from '../services/messagingPatternDetection'
import LegalComplianceNotice, { useLegalNotice, PersistentLegalWarning } from './LegalComplianceNotice'

interface DirectMessagesProps {
  selectedConversationId?: string
  onConversationSelect?: (conversationId: string) => void
}

// Mock data - replace with API calls
const mockUsers: User[] = [
  {
    id: 'user1',
    username: 'KratomExpert',
    email: 'expert@example.com',
    dateOfBirth: new Date('1990-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-01-01'),
    isVerified: true,
    role: 'trusted'
  },
  {
    id: 'user2',
    username: 'NewMember',
    email: 'new@example.com',
    dateOfBirth: new Date('1995-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-12-01'),
    isVerified: false,
    role: 'member'
  },
  {
    id: 'vendor1',
    username: 'QualityVendor',
    email: 'vendor@example.com',
    dateOfBirth: new Date('1985-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2023-06-01'),
    isVerified: true,
    role: 'verified_vendor'
  }
]

// Function to get mock conversations with current user ID
const getMockConversations = (currentUserId: string): Conversation[] => [
  {
    id: 'conv1',
    participants: [currentUserId, 'user1'],
    lastActivity: new Date('2024-12-08T14:30:00Z'),
    isArchived: false,
    lastMessage: {
      id: 'msg3',
      senderId: 'user1',
      receiverId: currentUserId,
      content: 'Thanks for the advice on red strains!',
      dateSent: new Date('2024-12-08T14:30:00Z'),
      isRead: false,
      isDeleted: false,
      conversationId: 'conv1'
    }
  },
  {
    id: 'conv2',
    participants: [currentUserId, 'vendor1'],
    lastActivity: new Date('2024-12-08T12:15:00Z'),
    isArchived: false,
    lastMessage: {
      id: 'msg2',
      senderId: currentUserId,
      receiverId: 'vendor1',
      content: 'Do you have any lab results for the Green Malay?',
      dateSent: new Date('2024-12-08T12:15:00Z'),
      isRead: true,
      isDeleted: false,
      conversationId: 'conv2'
    }
  }
]

// Function to get mock messages with current user ID
const getMockMessages = (currentUserId: string): DirectMessage[] => [
  {
    id: 'msg1',
    senderId: 'user1',
    receiverId: currentUserId,
    content: 'Hey! I saw your post about dosing questions. Red strains are great for relaxation - I usually take 3-4g in the evening.',
    dateSent: new Date('2024-12-08T14:00:00Z'),
    isRead: true,
    isDeleted: false,
    conversationId: 'conv1'
  },
  {
    id: 'msg2',
    senderId: currentUserId,
    receiverId: 'user1',
    content: 'That\'s really helpful, thank you! Any specific vendors you recommend for quality reds?',
    dateSent: new Date('2024-12-08T14:15:00Z'),
    isRead: true,
    isDeleted: false,
    conversationId: 'conv1'
  },
  {
    id: 'msg3',
    senderId: 'user1',
    receiverId: currentUserId,
    content: 'Thanks for the advice on red strains!',
    dateSent: new Date('2024-12-08T14:30:00Z'),
    isRead: false,
    isDeleted: false,
    conversationId: 'conv1'
  }
]

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function getUserInfo(userId: string): User | undefined {
  return mockUsers.find(u => u.id === userId)
}

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

export default function DirectMessages({ selectedConversationId, onConversationSelect }: DirectMessagesProps) {
  const { user } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentMessages, setCurrentMessages] = useState<DecryptedMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewMessage, setShowNewMessage] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isPrivateMode, setIsPrivateMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showRateLimitWarning, setShowRateLimitWarning] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const rateLimitInfo = useRateLimit(user?.id || '')
  const messagingNotice = useLegalNotice('messaging_compliance')
  const vendorContactNotice = useLegalNotice('vendor_contact_guidelines')

  // Initialize conversations and load secure messages
  useEffect(() => {
    if (user?.id) {
      loadConversations()
    }
  }, [user?.id])

  const loadConversations = async () => {
    if (!user?.id) return
    setLoading(true)
    
    try {
      // Get secure conversations
      const secureConversations = secureMessageService.getUserConversations(user.id)
      
      // Convert to legacy format for UI compatibility
      const legacyConversations: Conversation[] = secureConversations.map(conv => ({
        id: conv.id,
        participants: conv.participants,
        lastMessage: conv.lastMessage ? {
          id: conv.lastMessage.id,
          content: '[Encrypted]', // Don't show encrypted content in preview
          senderId: conv.lastMessage.senderId,
          receiverId: conv.lastMessage.receiverId,
          timestamp: new Date(conv.lastMessage.timestamp),
          conversationId: conv.id,
          isRead: true
        } : undefined,
        lastActivity: new Date(conv.lastActivity)
      }))
      
      setConversations(legacyConversations)
    } catch (error) {
      console.error('Failed to load conversations:', error)
      // Fallback to mock data
      setConversations(getMockConversations(user.id))
    } finally {
      setLoading(false)
    }
  }

  const selectedConversation = conversations.find(c => c.id === selectedConversationId)

  useEffect(() => {
    if (selectedConversationId && user?.id) {
      loadMessages(selectedConversationId)
    }
  }, [selectedConversationId, user?.id])

  const loadMessages = async (conversationId: string) => {
    if (!user?.id) return
    setLoading(true)
    
    try {
      const messages = await secureMessageService.getMessages(conversationId, user.id)
      setCurrentMessages(messages)
      
      // Check if conversation is in private mode
      const conversation = secureMessageService.getConversation(conversationId)
      if (conversation) {
        setIsPrivateMode(conversation.isPrivateMode)
      }
    } catch (error) {
      console.error('Failed to load messages:', error)
      // Fallback to mock data
      const allMessages = getMockMessages(user.id)
      const legacyMessages = allMessages.filter(m => m.conversationId === conversationId)
      const convertedMessages: DecryptedMessage[] = legacyMessages.map(m => ({
        id: m.id,
        content: m.content,
        senderId: m.senderId,
        receiverId: m.receiverId,
        timestamp: m.timestamp.getTime(),
        isPrivateMode: false,
        canDecrypt: true
      }))
      setCurrentMessages(convertedMessages)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentMessages])

  // Handle private mode toggle
  const handlePrivateModeToggle = async (isPrivate: boolean) => {
    if (!selectedConversationId) return
    
    try {
      await secureMessageService.togglePrivateMode(selectedConversationId, isPrivate)
      setIsPrivateMode(isPrivate)
    } catch (error) {
      console.error('Failed to toggle private mode:', error)
      alert('Failed to change encryption mode. Please try again.')
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user) return
    
    // Check rate limiting
    if (rateLimitInfo.isLimited) {
      setShowRateLimitWarning(true)
      return
    }
    
    const otherParticipant = selectedConversation.participants.find(p => p !== user.id)
    if (!otherParticipant) return
    
    setLoading(true)
    
    try {
      await secureMessageService.sendMessage(
        user.id,
        otherParticipant,
        newMessage.trim(),
        isPrivateMode
      )
      
      setNewMessage('')
      
      // Reload messages and conversations
      await loadMessages(selectedConversation.id)
      await loadConversations()
    } catch (error) {
      console.error('Failed to send message:', error)
      
      // Check if it's a rate limit error
      const currentRateLimit = messagingPatternDetection.getRateLimitInfo(user.id)
      if (currentRateLimit.isLimited) {
        setShowRateLimitWarning(true)
      } else {
        alert('Failed to send message. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const startNewConversation = async (targetUser: User) => {
    if (!user) return
    
    // Check rate limiting
    if (rateLimitInfo.isLimited) {
      setShowRateLimitWarning(true)
      return
    }
    
    setLoading(true)
    
    try {
      // Check if vendor approval is required
      if (await requiresVendorApproval(user.id, targetUser.id)) {
        // Create contact request instead of direct conversation
        const success = await createVendorContactRequest(
          user.id,
          targetUser.id,
          'Hello, I would like to connect with you.'
        )
        
        if (success) {
          alert(`Contact request sent to ${targetUser.username}. They will be notified and can approve your request.`)
          setShowNewMessage(false)
        } else {
          alert('Failed to send contact request. Please try again.')
        }
        return
      }
      
      const conversationId = await secureMessageService.createConversation(
        user.id,
        targetUser.id,
        false // Start with regular mode, user can toggle to private
      )
      
      await loadConversations()
      onConversationSelect?.(conversationId)
      setShowNewMessage(false)
    } catch (error) {
      console.error('Failed to create conversation:', error)
      
      // Check if it's a rate limit error
      const currentRateLimit = messagingPatternDetection.getRateLimitInfo(user.id)
      if (currentRateLimit.isLimited) {
        setShowRateLimitWarning(true)
      } else {
        alert('Failed to start conversation. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Categorize users for better organization
  const allUsers = mockUsers.filter(u => u.id !== user?.id)
  
  const filteredUsers = allUsers.filter(u => 
    u.username.toLowerCase().includes(searchQuery.toLowerCase())
  )
  
  // Mock friends list - in real app this would come from API/context
  const mockFriendIds = ['user1', 'vendor1', 'user2'] // These should be actual friend IDs
  
  // Group users by category with friends prioritized
  const recentContacts = filteredUsers.filter(u => 
    conversations.some(conv => conv.participants.includes(u.id))
  )
  
  const friendUsers = filteredUsers.filter(u => 
    mockFriendIds.includes(u.id) && !recentContacts.includes(u)
  )
  
  const vendorUsers = filteredUsers.filter(u => 
    u.role === 'verified_vendor' && !recentContacts.includes(u) && !friendUsers.includes(u)
  )
  
  const trustedUsers = filteredUsers.filter(u => 
    u.role === 'trusted' && !recentContacts.includes(u) && !friendUsers.includes(u) && !vendorUsers.includes(u)
  )
  
  const otherUsers = filteredUsers.filter(u => 
    !recentContacts.includes(u) && !friendUsers.includes(u) && !vendorUsers.includes(u) && !trustedUsers.includes(u)
  )

  const unreadCount = conversations.reduce((count, conv) => {
    return count + (conv.lastMessage && !conv.lastMessage.isRead && conv.lastMessage.receiverId === user?.id ? 1 : 0)
  }, 0)

  return (
    <div className="direct-messages">
      <div className="messages-layout">
        {/* Conversations Sidebar */}
        <div className="conversations-sidebar">
          <div className="sidebar-header">
            <h2>Messages {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}</h2>
            <button 
              onClick={() => setShowNewMessage(true)}
              className="new-message-btn"
              title="Start new conversation"
            >
              ✉️
            </button>
          </div>

          {/* Legal Compliance Notice for Messaging */}
          {!messagingNotice.isDismissed && (
            <LegalComplianceNotice 
              context="messaging"
              onDismiss={messagingNotice.dismissNotice}
            />
          )}

          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="no-conversations">
                <div className="no-conversations-icon">💬</div>
                <h3>No conversations</h3>
                <p>Start chatting with community members</p>
                <button onClick={() => setShowNewMessage(true)} className="btn-primary">
                  New Conversation
                </button>
              </div>
            ) : (
              conversations.map(conversation => {
                const otherParticipant = getUserInfo(
                  conversation.participants.find(p => p !== user?.id) || ''
                )
                const isSelected = conversation.id === selectedConversationId
                const hasUnread = conversation.lastMessage && 
                  !conversation.lastMessage.isRead && 
                  conversation.lastMessage.receiverId === user?.id

                return (
                  <div
                    key={conversation.id}
                    className={`conversation-item ${isSelected ? 'selected' : ''} ${hasUnread ? 'unread' : ''}`}
                    onClick={() => onConversationSelect?.(conversation.id)}
                  >
                    <div className="conversation-avatar-container">
                      <div className="conversation-avatar">
                        <div className="avatar-circle">
                          {otherParticipant?.username[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="online-status online"></div>
                      </div>
                      {hasUnread && <div className="unread-dot" />}
                    </div>
                    
                    <div className="conversation-details">
                      <div className="conversation-header">
                        <span 
                          className="participant-name"
                          style={{ color: getRoleColor(otherParticipant?.role || 'member') }}
                        >
                          {getRoleBadge(otherParticipant?.role || 'member')} {otherParticipant?.username || 'Unknown User'}
                        </span>
                        <span className="last-activity">
                          {formatTimestamp(conversation.lastActivity)}
                        </span>
                      </div>
                      
                      {conversation.lastMessage && (
                        <div className="last-message-container">
                          <div className="message-preview">
                            <span className={`message-sender ${conversation.lastMessage.senderId === user?.id ? 'you' : 'them'}`}>
                              {conversation.lastMessage.senderId === user?.id ? 'You: ' : ''}
                            </span>
                            <span className="message-text">
                              {conversation.lastMessage.content.length > 50 
                                ? conversation.lastMessage.content.slice(0, 50) + '...'
                                : conversation.lastMessage.content
                              }
                            </span>
                          </div>
                          {hasUnread && (
                            <div className="unread-badge">
                              <span className="unread-count">1</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="messages-area">
          {selectedConversationId ? (
            <>
              {/* Messages Header */}
              <div className="messages-header">
                {selectedConversation && (
                  <>
                    {(() => {
                      const otherParticipant = getUserInfo(
                        selectedConversation.participants.find(p => p !== user?.id) || ''
                      )
                      return (
                        <div className="conversation-header-info">
                          <div className="participant-avatar-large">
                            <div className="avatar-circle">
                              {otherParticipant?.username[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="online-status online"></div>
                          </div>
                          <div className="participant-details">
                            <Link 
                              to={`/profile/${otherParticipant?.id}`}
                              className="participant-name"
                              style={{ color: getRoleColor(otherParticipant?.role || 'member') }}
                            >
                              {getRoleBadge(otherParticipant?.role || 'member')} {otherParticipant?.username || 'Unknown User'}
                            </Link>
                            <div className="participant-status">
                              <span className="status-dot online"></span>
                              <span className="status-text">Active now</span>
                              <span className="role-text">• {otherParticipant?.role || 'member'}</span>
                            </div>
                          </div>
                          <div className="conversation-actions">
                            <button className="header-action-btn" title="Voice call" disabled>
                              📞
                            </button>
                            <button className="header-action-btn" title="Video call" disabled>
                              📹
                            </button>
                            <button className="header-action-btn" title="More options">
                              ⋯
                            </button>
                          </div>
                        </div>
                      )
                    })()}
                  </>
                )}
              </div>
              
              {/* Rate Limit Warning */}
              {showRateLimitWarning && (
                <RateLimitWarning 
                  userId={user?.id || ''}
                  onDismiss={() => setShowRateLimitWarning(false)}
                />
              )}
              
              {/* Persistent Legal Warning for Active Conversation */}
              <PersistentLegalWarning type="messaging" />
              
              {/* Private Mode Toggle */}
              <PrivateModeToggle 
                isPrivateMode={isPrivateMode}
                onToggle={handlePrivateModeToggle}
                disabled={loading || rateLimitInfo.isLimited}
              />

              {/* Messages List */}
              <div className="messages-list">
                {currentMessages.length === 0 ? (
                  <div className="no-messages">
                    <div className="no-messages-icon">💬</div>
                    <h3>No messages yet</h3>
                    <p>Start the conversation by sending a message below!</p>
                  </div>
                ) : (
                  currentMessages.map((message, index) => {
                    const isOwnMessage = message.senderId === user?.id
                    const sender = getUserInfo(message.senderId)
                    const prevMessage = index > 0 ? currentMessages[index - 1] : null
                    const nextMessage = index < currentMessages.length - 1 ? currentMessages[index + 1] : null
                    
                    const messageDate = new Date(message.timestamp)
                    
                    // Show date separator
                    const showDate = index === 0 || 
                      messageDate.toDateString() !== 
                      new Date(prevMessage!.timestamp).toDateString()
                    
                    // Show avatar and name (for consecutive messages from same sender)
                    const showAvatar = !isOwnMessage && (
                      index === 0 || 
                      prevMessage?.senderId !== message.senderId ||
                      showDate
                    )
                    
                    // Show timestamp (for messages with time gaps or last in group)
                    const showTime = !nextMessage || 
                      nextMessage.senderId !== message.senderId ||
                      (nextMessage.timestamp - message.timestamp) > 300000 // 5 minutes
                    
                    return (
                      <div key={message.id} className="message-wrapper">
                        {showDate && (
                          <div className="date-divider">
                            <div className="divider-line"></div>
                            <span className="divider-text">
                              {messageDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <div className="divider-line"></div>
                          </div>
                        )}
                        
                        <MessageBubble
                          message={{
                            id: message.id,
                            content: message.content,
                            senderId: message.senderId,
                            receiverId: message.receiverId,
                            dateSent: messageDate,
                            isRead: true,
                            isDeleted: false,
                            conversationId: selectedConversationId || ''
                          }}
                          sender={sender}
                          isOwnMessage={isOwnMessage}
                          showAvatar={showAvatar}
                          showTime={showTime}
                          getRoleColor={getRoleColor}
                          getRoleBadge={getRoleBadge}
                        />
                        
                        {/* Show encryption indicator for private messages */}
                        {message.isPrivateMode && (
                          <div className={`encryption-indicator ${isOwnMessage ? 'own' : 'other'}`}>
                            🔒 End-to-end encrypted
                          </div>
                        )}
                        
                        {/* Show decryption failed indicator */}
                        {!message.canDecrypt && (
                          <div className={`encryption-indicator error ${isOwnMessage ? 'own' : 'other'}`}>
                            ⚠️ Unable to decrypt message
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                <div className="input-box">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message ${selectedConversation ? getUserInfo(selectedConversation.participants.find(p => p !== user?.id) || '')?.username || 'user' : 'user'}...`}
                    className="message-input"
                    rows={1}
                    disabled={rateLimitInfo.isLimited}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (!rateLimitInfo.isLimited) {
                          sendMessage()
                        } else {
                          setShowRateLimitWarning(true)
                        }
                      }
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement
                      target.style.height = 'auto'
                      target.style.height = Math.min(target.scrollHeight, 120) + 'px'
                    }}
                    maxLength={1000}
                  />
                  
                  <div className="input-actions">
                    <button className="action-btn" title="Attach file" disabled>
                      📎
                    </button>
                    <button className="action-btn" title="Emoji" disabled>
                      😊
                    </button>
                    {newMessage.trim() && (
                      <button 
                        onClick={() => {
                          if (rateLimitInfo.isLimited) {
                            setShowRateLimitWarning(true)
                          } else {
                            sendMessage()
                          }
                        }}
                        disabled={loading}
                        className={`send-btn ${rateLimitInfo.isLimited ? 'rate-limited' : ''}`}
                        title={rateLimitInfo.isLimited ? 'Rate limited - click for details' : 'Send message'}
                      >
                        {loading ? '⏳' : rateLimitInfo.isLimited ? '🚫' : 'Send'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <div className="empty-state">
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the sidebar or start a new one</p>
                <button onClick={() => setShowNewMessage(true)} className="btn-primary">
                  New Message
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessage && (
        <div className="new-message-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Start New Conversation</h3>
              <button 
                onClick={() => setShowNewMessage(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            
            {/* Vendor Contact Guidelines Notice */}
            {!vendorContactNotice.isDismissed && (
              <LegalComplianceNotice 
                context="vendor_contact"
                onDismiss={vendorContactNotice.dismissNotice}
              />
            )}
            
            <div className="modal-body">
              <div className="user-search">
                <input
                  type="text"
                  placeholder="Search community members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="users-list">
                {searchQuery.trim() === '' ? (
                  // Show categorized users when not searching
                  <>
                    {recentContacts.length > 0 && (
                      <div className="user-category">
                        <h4 className="category-title">Recent Contacts</h4>
                        <div className="category-users">
                          {recentContacts.map(targetUser => (
                            <div
                              key={targetUser.id}
                              className="user-item"
                              onClick={() => startNewConversation(targetUser)}
                            >
                              <div className="user-avatar">
                                <div className="avatar-placeholder">
                                  {targetUser.username[0].toUpperCase()}
                                </div>
                                <div className="online-indicator">🟢</div>
                              </div>
                              <div className="user-info">
                                <span 
                                  className="username"
                                  style={{ color: getRoleColor(targetUser.role) }}
                                >
                                  {getRoleBadge(targetUser.role)} {targetUser.username}
                                </span>
                                <span className="user-role">{targetUser.role.replace('_', ' ')}</span>
                              </div>
                              <div className="user-actions">
                                <span className="recent-badge">Recent</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {friendUsers.length > 0 && (
                      <div className="user-category">
                        <h4 className="category-title">Friends</h4>
                        <div className="category-users">
                          {friendUsers.map(targetUser => (
                            <div
                              key={targetUser.id}
                              className="user-item"
                              onClick={() => startNewConversation(targetUser)}
                            >
                              <div className="user-avatar">
                                <div className="avatar-placeholder">
                                  {targetUser.username[0].toUpperCase()}
                                </div>
                                <div className="online-indicator">🟢</div>
                              </div>
                              <div className="user-info">
                                <span 
                                  className="username"
                                  style={{ color: getRoleColor(targetUser.role) }}
                                >
                                  {getRoleBadge(targetUser.role)} {targetUser.username}
                                </span>
                                <span className="user-role">{targetUser.role.replace('_', ' ')}</span>
                              </div>
                              <div className="user-actions">
                                <span className="friend-badge">Friend</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {vendorUsers.length > 0 && (
                      <div className="user-category">
                        <h4 className="category-title">Verified Vendors</h4>
                        <div className="category-users">
                          {vendorUsers.map(targetUser => (
                            <div
                              key={targetUser.id}
                              className="user-item"
                              onClick={() => startNewConversation(targetUser)}
                            >
                              <div className="user-avatar">
                                <div className="avatar-placeholder">
                                  {targetUser.username[0].toUpperCase()}
                                </div>
                                <div className="online-indicator">🟢</div>
                              </div>
                              <div className="user-info">
                                <span 
                                  className="username"
                                  style={{ color: getRoleColor(targetUser.role) }}
                                >
                                  {getRoleBadge(targetUser.role)} {targetUser.username}
                                </span>
                                <span className="user-role">Verified Vendor</span>
                              </div>
                              <div className="user-actions">
                                <span className="vendor-badge">Vendor</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {trustedUsers.length > 0 && (
                      <div className="user-category">
                        <h4 className="category-title">Trusted Members</h4>
                        <div className="category-users">
                          {trustedUsers.map(targetUser => (
                            <div
                              key={targetUser.id}
                              className="user-item"
                              onClick={() => startNewConversation(targetUser)}
                            >
                              <div className="user-avatar">
                                <div className="avatar-placeholder">
                                  {targetUser.username[0].toUpperCase()}
                                </div>
                                <div className="online-indicator">🟢</div>
                              </div>
                              <div className="user-info">
                                <span 
                                  className="username"
                                  style={{ color: getRoleColor(targetUser.role) }}
                                >
                                  {getRoleBadge(targetUser.role)} {targetUser.username}
                                </span>
                                <span className="user-role">Trusted Member</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {otherUsers.length > 0 && (
                      <div className="user-category">
                        <h4 className="category-title">Community Members</h4>
                        <div className="category-users">
                          {otherUsers.slice(0, 5).map(targetUser => (
                            <div
                              key={targetUser.id}
                              className="user-item"
                              onClick={() => startNewConversation(targetUser)}
                            >
                              <div className="user-avatar">
                                <div className="avatar-placeholder">
                                  {targetUser.username[0].toUpperCase()}
                                </div>
                                <div className="online-indicator">🟢</div>
                              </div>
                              <div className="user-info">
                                <span 
                                  className="username"
                                  style={{ color: getRoleColor(targetUser.role) }}
                                >
                                  {getRoleBadge(targetUser.role)} {targetUser.username}
                                </span>
                                <span className="user-role">Member</span>
                              </div>
                            </div>
                          ))}
                          {otherUsers.length > 5 && (
                            <div className="more-users-hint">
                              <span>+{otherUsers.length - 5} more members. Use search to find specific users.</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {filteredUsers.length === 0 && (
                      <div className="no-users-found">
                        <div className="no-users-icon">👥</div>
                        <h4>No users available</h4>
                        <p>There are no community members to message at the moment.</p>
                      </div>
                    )}
                  </>
                ) : (
                  // Show search results
                  <>
                    <div className="search-results-header">
                      <h4>Search Results ({filteredUsers.length})</h4>
                    </div>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(targetUser => (
                        <div
                          key={targetUser.id}
                          className="user-item"
                          onClick={() => startNewConversation(targetUser)}
                        >
                          <div className="user-avatar">
                            <div className="avatar-placeholder">
                              {targetUser.username[0].toUpperCase()}
                            </div>
                            <div className="online-indicator">🟢</div>
                          </div>
                          <div className="user-info">
                            <span 
                              className="username"
                              style={{ color: getRoleColor(targetUser.role) }}
                            >
                              {getRoleBadge(targetUser.role)} {targetUser.username}
                            </span>
                            <span className="user-role">{targetUser.role.replace('_', ' ')}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-users-found">
                        <div className="no-users-icon">🔍</div>
                        <h4>No users found</h4>
                        <p>No users match your search for "{searchQuery}"</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="modal-overlay" onClick={() => setShowNewMessage(false)} />
        </div>
      )}
    </div>
  )
}